import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Bell, Building2, Settings2 } from "lucide-react";

import SectionTabs from "../../../../shared/ui/tabs/SectionTabs";
import { useCenter } from "../../../center";
import DefaultPaymentLineCard from "./DefaultPaymentLineCard";
import PaymentNotificationSettings from "./PaymentNotificationSettings";
import StripeConnectCard from "./StripeConnectCard";

type Section = "general" | "notifications";

/**
 * Everything a therapist configures about Payments, rendered as the Payments
 * tab of practice settings — and only once the therapist has adopted the tool.
 *
 * This is also where Stripe lands after OAuth (`?stripe=connected|error`): the
 * backend returns to `/payments/settings`, which redirects here with the query
 * string intact.
 *
 * A therapist in a centre still has their own settings here — for their own
 * patients. The centre's patients are charged with the centre's Stripe and
 * the centre's defaults, and the panel says so rather than letting a therapist
 * believe these settings reach them.
 *
 * Two sections: how the therapist is paid, and every message Payments sends —
 * reminders included, next to the other notifications they belong with.
 */
const PaymentSettingsPanel: React.FC = () => {
    const { t } = useTranslation();
    const { center, isAdmin } = useCenter();
    const [section, setSection] = useState<Section>("general");

    return (
        <div className="flex flex-col gap-6">
            <SectionTabs
                tabs={[
                    {
                        id: "general",
                        label: t("payments.settings.sections.general"),
                        icon: Settings2,
                    },
                    {
                        id: "notifications",
                        label: t("payments.settings.sections.notifications"),
                        icon: Bell,
                    },
                ]}
                activeTab={section}
                onChange={setSection}
                label={t("payments.settings.title")}
                className="mb-0"
            />

            {section === "general" && center && (
                <div
                    role="note"
                    className="flex gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-800 dark:bg-white/[0.03]"
                >
                    <Building2
                        size={18}
                        className="mt-0.5 shrink-0 text-content-tertiary"
                        aria-hidden="true"
                    />
                    <div>
                        <p className="font-semibold text-content-primary">
                            {t("payments.settings.centerManagedTitle", { center: center.name })}
                        </p>
                        <p className="mt-1 text-content-tertiary">
                            {t("payments.settings.centerManagedBody", { center: center.name })}
                        </p>
                        {isAdmin && (
                            <Link
                                to="/center/payments"
                                className="mt-2 inline-block font-medium text-brand-600 hover:underline dark:text-brand-400"
                            >
                                {t("payments.settings.centerManagedLink")}
                            </Link>
                        )}
                    </div>
                </div>
            )}

            {section === "general" && (
                <>
                    {/* Hides itself when the deployment has no Stripe credentials,
                        rather than offering a button that cannot work. */}
                    <StripeConnectCard />

                    <DefaultPaymentLineCard />
                </>
            )}

            {section === "notifications" && <PaymentNotificationSettings />}
        </div>
    );
};

export default PaymentSettingsPanel;
